import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, UserCog, UsersRound, Building, ShieldOff, UserCheck, UserX,
  Palette, FileText, ClipboardCheck, ScrollText, Plus, Trash2, Loader2, Search, Edit3, ExternalLink, Mail, Phone, MapPin,
  Package, Calendar, AlertTriangle, PowerOff, Receipt, CheckCircle2, Users,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

const STATUSES = ["active", "inactive", "suspended", "pending_setup"];

const DEFAULT_DEPTS = [
  "Nursing","ICU","OT","Emergency","Laboratory","Pharmacy","Radiology","Quality",
  "Infection Control","HR","Administration","Housekeeping","Biomedical Engineering",
  "CSSD","Dietary","Front Office","Billing","MRD",
];

const ACTIVE_KEYS = new Set(["organization_created", "admin_assigned", "departments_created"]);

const ONBOARDING_LABELS = [
  ["organization_created", "Organization Created"],
  ["admin_assigned", "Admin Assigned"],
  ["departments_created", "Departments Created"],
  ["staff_enrollment_pending", "Staff Enrollment (Phase 4)"],
  ["course_mapping_pending", "Course Mapping (Phase 5)"],
  ["training_pending", "Training (Phase 6)"],
  ["go_live_pending", "Go Live (Phase 9)"],
  ["active_training_started", "Active Training"],
];

export default function AdminOrganizationDetailPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [dash, setDash] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [history, setHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);

  const [statusDlg, setStatusDlg] = useState(null); // {next, title}
  const [statusReason, setStatusReason] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("create");
  const [assignForm, setAssignForm] = useState({
    first_name: "", last_name: "", email: "", mobile: "", designation: "Institute Admin", password: "", confirm: "",
    user_id: "",
  });
  const [existingAdmins, setExistingAdmins] = useState([]);
  const [assignError, setAssignError] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);

  const [deptDlg, setDeptDlg] = useState(null); // dept or {create:true}
  const [deptForm, setDeptForm] = useState({ department_name: "", department_code: "", description: "", status: "active" });
  const [deptSaving, setDeptSaving] = useState(false);

  const [docDlg, setDocDlg] = useState(false);
  const [docForm, setDocForm] = useState({ document_type: "MoU", document_name: "", document_file_path: "", remarks: "" });
  const [docSaving, setDocSaving] = useState(false);

  const [savingBranding, setSavingBranding] = useState(false);

  // Subscription plan assignment
  const [subInfo, setSubInfo] = useState(null);
  const [subHistory, setSubHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planDlgOpen, setPlanDlgOpen] = useState(false);
  const [planForm, setPlanForm] = useState({
    plan_id: "", seats_purchased: 50, start_date: "", end_date: "",
    payment_status: "paid", discount_percent: 0, coupon_code: "",
  });
  const [planSaving, setPlanSaving] = useState(false);
  const [couponPreview, setCouponPreview] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  // Invoice cancel/delete dialog (Super Admin)
  const [invAction, setInvAction] = useState(null); // {kind:"cancel"|"delete", row}
  const [invReason, setInvReason] = useState("");
  const [invNotify, setInvNotify] = useState(true);
  const [invSubmitting, setInvSubmitting] = useState(false);
  const onInvoiceAction = (kind, row) => {
    setInvAction({ kind, row });
    setInvReason("");
    setInvNotify(true);
  };
  const closeInvAction = () => { if (!invSubmitting) { setInvAction(null); setInvReason(""); } };
  const confirmInvAction = async () => {
    if (!invAction?.row?.invoice_id) return;
    setInvSubmitting(true);
    try {
      await api.post(`/admin/invoices/${invAction.row.invoice_id}/${invAction.kind}`,
        { reason: invReason, notify_institute: invNotify });
      toast.success(invAction.kind === "cancel" ? "Invoice cancelled" : "Invoice deleted");
      setInvAction(null); setInvReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setInvSubmitting(false); }
  };

  // Bulk invoice for institute (Super Admin billing hospital for a course)
  const [billOpen, setBillOpen] = useState(false);
  const [mappedCourses, setMappedCourses] = useState([]);
  const [orgStaff, setOrgStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState({});
  const [staffQuery, setStaffQuery] = useState("");
  const [billForm, setBillForm] = useState({
    course_id: "", unit_price: "", payment_method: "bank_transfer",
    payment_reference: "", send_email: true,
  });
  const [billing, setBilling] = useState(false);

  const filteredStaff = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    if (!q) return orgStaff;
    return orgStaff.filter((s) =>
      (s.full_name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.department_name || "").toLowerCase().includes(q) ||
      (s.designation || "").toLowerCase().includes(q)
    );
  }, [orgStaff, staffQuery]);
  const selectedCount = useMemo(
    () => Object.values(selectedStaff).filter(Boolean).length, [selectedStaff]
  );

  const openBillDialog = async () => {
    // Open dialog immediately so the user gets feedback; load data in background.
    setStaffQuery("");
    setMappedCourses([]);
    setOrgStaff([]);
    setSelectedStaff({});
    setBillForm({ course_id: "", unit_price: "",
                  payment_method: "bank_transfer", payment_reference: "", send_email: true });
    setBillOpen(true);
    try {
      const [mc, us] = await Promise.all([
        api.get(`/admin/organizations/${orgId}/course-mappings`),
        api.get(`/admin/users?organization_id=${orgId}&page_size=500`),
      ]);
      const mappings = mc.data?.data?.mappings || mc.data?.data?.course_mappings || [];
      setMappedCourses(mappings);
      const users = us.data?.data?.users || [];
      const staff = users.filter((u) => !["hospital_admin", "super_admin"].includes(u.role_code));
      setOrgStaff(staff);
      const pre = {}; staff.forEach((s) => { pre[s.id] = true; });
      setSelectedStaff(pre);
      setBillForm((f) => ({ ...f, course_id: mappings[0]?.course_id || "" }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleStaff = (uid) => setSelectedStaff((prev) => ({ ...prev, [uid]: !prev[uid] }));
  const setAllStaff = (val) => {
    const next = {}; filteredStaff.forEach((s) => { next[s.id] = val; });
    setSelectedStaff((prev) => ({ ...prev, ...next }));
  };
  const submitBulkBill = async () => {
    const ids = Object.entries(selectedStaff).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.error("Pick at least one staff member."); return; }
    if (!billForm.course_id) { toast.error("Pick a course."); return; }
    const price = Number(billForm.unit_price);
    if (!price || price <= 0) { toast.error("Enter a valid unit price."); return; }
    setBilling(true);
    try {
      const { data } = await api.post(`/admin/organizations/${orgId}/bulk-invoice`, {
        course_id: billForm.course_id, user_ids: ids,
        unit_price: price,
        payment_method: billForm.payment_method,
        payment_reference: billForm.payment_reference || "",
        send_email: billForm.send_email,
      });
      const invNo = data?.data?.invoice?.invoice_number;
      toast.success(`Invoice ${invNo} created for ${ids.length} staff${billForm.send_email ? " · emailed to institute" : ""}.`);
      setBillOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBilling(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, depts, adm, dsh, onb, brand, docs, aud, sub, hist] = await Promise.all([
        api.get(`/admin/organizations-summary/${orgId}`),
        api.get(`/admin/organizations/${orgId}/departments`),
        api.get(`/admin/organizations/${orgId}/admins`),
        api.get(`/admin/organizations/${orgId}/dashboard-summary`),
        api.get(`/admin/organizations/${orgId}/onboarding`),
        api.get(`/admin/organizations/${orgId}/branding`),
        api.get(`/admin/organizations/${orgId}/documents`),
        api.get(`/admin/organizations/${orgId}/audit-logs`),
        api.get(`/admin/organizations/${orgId}/subscription`).catch(() => ({ data: { data: null } })),
        api.get(`/admin/organizations/${orgId}/subscription-history`).catch(() => ({ data: { data: { history: [] } } })),
      ]);
      setOrg(detail.data.data.organization);
      setDepartments(depts.data.data.departments);
      setAdmins(adm.data.data.admins);
      setDash(dsh.data.data.totals);
      setOnboarding(onb.data.data.onboarding);
      setBranding(brand.data.data.branding);
      setDocuments(docs.data.data.documents);
      setHistory(aud.data.data.status_history || []);
      setSubInfo(sub.data.data || null);
      setSubHistory(hist.data.data?.history || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const openPlanDialog = async () => {
    try {
      const { data } = await api.get("/admin/subscription-plans");
      const active = (data.data?.plans || []).filter((p) => p.status === "active");
      setPlans(active);
      const today = new Date().toISOString().slice(0, 10);
      const currentPlanId = subInfo?.subscription?.plan_id || "";
      const seats = subInfo?.subscription?.seats_purchased || (active[0]?.max_users || 50);
      setPlanForm({
        plan_id: currentPlanId || active[0]?.id || "",
        seats_purchased: seats,
        start_date: today,
        end_date: "",
        payment_status: "paid",
        discount_percent: 0,
        coupon_code: "",
      });
      setCouponPreview(null);
      setCouponError("");
      setPlanDlgOpen(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitPlan = async () => {
    if (!planForm.plan_id) { toast.error("Pick a plan first."); return; }
    setPlanSaving(true);
    try {
      const chosen = plans.find((p) => p.id === planForm.plan_id);
      const start = planForm.start_date || new Date().toISOString().slice(0, 10);
      let end = planForm.end_date;
      if (!end && chosen) {
        const d = new Date(start);
        d.setDate(d.getDate() + (chosen.validity_days || 365));
        end = d.toISOString().slice(0, 10);
      }
      await api.post(`/admin/organizations/${orgId}/subscription`, {
        plan_id: planForm.plan_id,
        seats_purchased: Number(planForm.seats_purchased) || 0,
        start_date: new Date(start).toISOString(),
        end_date: new Date(end).toISOString(),
        payment_status: planForm.payment_status,
        subscription_status: "active",
        discount_percent: Number(planForm.discount_percent) || 0,
        coupon_code: (planForm.coupon_code || "").trim().toUpperCase(),
      });
      toast.success("Subscription plan assigned.");
      setPlanDlgOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPlanSaving(false); }
  };

  const confirmDeactivate = async () => {
    setDeactivating(true);
    try {
      await api.patch(`/admin/organizations/${orgId}/subscription/deactivate`);
      toast.success("Subscription deactivated.");
      setDeactivateOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeactivating(false); }
  };

  const applyCoupon = async () => {
    const code = (planForm.coupon_code || "").trim().toUpperCase();
    if (!code) { setCouponError("Enter a coupon code."); return; }
    if (!planForm.plan_id) { setCouponError("Pick a plan first."); return; }
    setValidatingCoupon(true); setCouponError("");
    try {
      const { data } = await api.post("/admin/subscription-coupons/validate", {
        coupon_code: code, plan_id: planForm.plan_id, organization_id: orgId,
      });
      setCouponPreview(data.data);
      // Coupon overrides percent; clear loyalty percent so UI stays honest.
      setPlanForm((f) => ({ ...f, discount_percent: 0 }));
      toast.success(`Coupon ${code} applied · ₹${data.data.discount.toLocaleString("en-IN")} off`);
    } catch (err) {
      setCouponPreview(null);
      setCouponError(formatApiError(err));
    } finally { setValidatingCoupon(false); }
  };

  const clearCoupon = () => {
    setCouponPreview(null); setCouponError("");
    setPlanForm((f) => ({ ...f, coupon_code: "" }));
  };

  const applyStatusChange = async () => {
    if (!statusDlg) return;
    if (!statusReason.trim() || statusReason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters.");
      return;
    }
    setSavingStatus(true);
    try {
      await api.patch(`/admin/organizations/${orgId}/status`, { status: statusDlg.next, reason: statusReason });
      toast.success(`Status changed to ${statusDlg.next.replace(/_/g, " ")}.`);
      setStatusDlg(null); setStatusReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingStatus(false);
    }
  };

  const openAssign = async () => {
    setAssignOpen(true);
    setAssignError("");
    setAssignMode("create");
    setAssignForm({ first_name: "", last_name: "", email: "", mobile: "", designation: "Institute Admin", password: "", confirm: "", user_id: "" });
    try {
      const { data } = await api.get("/admin/users", { params: { role: "hospital_admin", page_size: 200 } });
      setExistingAdmins(data.data.users.filter((u) => !u.organization_id || u.organization_id === orgId));
    } catch { /* silent */ }
  };

  const submitAssign = async () => {
    setAssignError("");
    setAssignSaving(true);
    try {
      if (assignMode === "create") {
        if (assignForm.password !== assignForm.confirm) {
          setAssignError("Passwords do not match."); setAssignSaving(false); return;
        }
        await api.post(`/admin/organizations/${orgId}/create-admin`, {
          first_name: assignForm.first_name, last_name: assignForm.last_name,
          email: assignForm.email, mobile: assignForm.mobile,
          designation: assignForm.designation, password: assignForm.password,
          account_status: "active",
        });
        toast.success("Admin created and assigned.");
      } else {
        if (!assignForm.user_id) { setAssignError("Select a user."); setAssignSaving(false); return; }
        await api.post(`/admin/organizations/${orgId}/assign-existing-admin`, { user_id: assignForm.user_id });
        toast.success("Admin assigned.");
      }
      setAssignOpen(false); load();
    } catch (err) { setAssignError(formatApiError(err)); }
    finally { setAssignSaving(false); }
  };

  const openDeptCreate = () => {
    setDeptForm({ department_name: "", department_code: "", description: "", status: "active" });
    setDeptDlg({ create: true });
  };
  const openDeptEdit = (d) => {
    setDeptForm({ department_name: d.department_name, department_code: d.department_code, description: d.description || "", status: d.status });
    setDeptDlg(d);
  };
  const submitDept = async () => {
    setDeptSaving(true);
    try {
      if (deptDlg?.create) {
        await api.post(`/admin/organizations/${orgId}/departments`, deptForm);
        toast.success("Department created.");
      } else {
        await api.put(`/admin/organizations/${orgId}/departments/${deptDlg.id}`, deptForm);
        toast.success("Department updated.");
      }
      setDeptDlg(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeptSaving(false); }
  };

  const toggleDept = async (d) => {
    try {
      await api.patch(`/admin/organizations/${orgId}/departments/${d.id}/status`,
        { status: d.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const seedDefaultDepts = async () => {
    const codes = new Set(departments.map((d) => d.department_code));
    let created = 0;
    for (const name of DEFAULT_DEPTS) {
      const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, "_");
      if (codes.has(code)) continue;
      try {
        await api.post(`/admin/organizations/${orgId}/departments`, {
          department_name: name, department_code: code, status: "active",
        });
        created++;
      } catch { /* skip clashes */ }
    }
    toast.success(`${created} healthcare departments seeded.`);
    load();
  };

  const submitBranding = async () => {
    setSavingBranding(true);
    try {
      await api.put(`/admin/organizations/${orgId}/branding`, branding);
      toast.success("Branding saved.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingBranding(false); }
  };

  const submitDoc = async () => {
    setDocSaving(true);
    try {
      await api.post(`/admin/organizations/${orgId}/documents`, docForm);
      toast.success("Document added.");
      setDocDlg(false);
      setDocForm({ document_type: "MoU", document_name: "", document_file_path: "", remarks: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDocSaving(false); }
  };
  const deleteDoc = async (id) => {
    try { await api.delete(`/admin/organizations/${orgId}/documents/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !org) {
    return (
      <DashboardLayout title="Organization" breadcrumbs={["Platform", "Organizations"]}>
        <LoadingBlock label="Loading organization…" />
      </DashboardLayout>
    );
  }

  const primaryAdmin = admins[0];

  return (
    <DashboardLayout title={org.organization_name} breadcrumbs={["Platform", "Organizations", org.organization_name]}>
      <button onClick={() => navigate("/app/super-admin/organizations")} className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#101876]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to organizations
      </button>

      {/* Header card */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-org-detail">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ backgroundColor: `${org.primary_color}15`, color: org.primary_color }}>
              <Building className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">{(org.organization_type || "").replace(/_/g, " ")}</div>
              <h2 className="text-2xl font-black text-slate-900 truncate" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{org.organization_name}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {org.city}, {org.state}</span>
                {org.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {org.email}</span>}
                {org.mobile && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {org.mobile}</span>}
                <span className="font-semibold text-slate-700">{org.organization_code}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={org.status} />
            <Link to={`/app/super-admin/organizations/${orgId}/edit`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid="admin-org-edit-btn">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Link>
            <button onClick={openAssign} className="inline-flex items-center gap-1.5 rounded-md bg-[#329112] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#256b0d]" data-testid="admin-org-assign-admin-btn">
              <UserCog className="h-3.5 w-3.5" /> {primaryAdmin ? "Reassign admin" : "Assign admin"}
            </button>
            {org.status !== "active" && <button onClick={() => setStatusDlg({ next: "active", title: `Activate ${org.organization_name}?` })} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="admin-org-activate-btn"><UserCheck className="h-3.5 w-3.5" /> Activate</button>}
            {org.status !== "inactive" && <button onClick={() => setStatusDlg({ next: "inactive", title: `Deactivate ${org.organization_name}?` })} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><UserX className="h-3.5 w-3.5" /> Deactivate</button>}
            {org.status !== "suspended" && <button onClick={() => setStatusDlg({ next: "suspended", title: `Suspend ${org.organization_name}?` })} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50" data-testid="admin-org-suspend-btn"><ShieldOff className="h-3.5 w-3.5" /> Suspend</button>}
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {[
          ["Users", dash.users], ["Admins", dash.admins],
          ["Dept. Heads", dash.department_heads], ["Faculty", dash.faculty],
          ["Learners", dash.students], ["Departments", dash.departments],
        ].map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{k}</div>
            <div className="mt-1 text-2xl font-black text-slate-900">{v ?? 0}</div>
          </div>
        ))}
      </section>

      {/* Tabs */}
      <div className="mt-6">
        <Tabs defaultValue="overview">
          <TabsList className="bg-white border border-slate-200 rounded-lg p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Overview</TabsTrigger>
            <TabsTrigger value="departments" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Departments</TabsTrigger>
            <TabsTrigger value="admins" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Admins</TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Branding</TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Documents</TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-[#101876] data-[state=active]:text-white">Status history</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            {/* Subscription plan card */}
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-org-subscription-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876]"><Package className="h-5 w-5" /></div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Subscription plan</div>
                    <div className="text-sm font-bold text-slate-900">
                      {subInfo?.plan?.plan_name || "No plan assigned"}
                    </div>
                    {subInfo?.subscription && (
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />
                          {(subInfo.subscription.start_date || "").slice(0, 10)} → {(subInfo.subscription.end_date || "").slice(0, 10)}
                        </span>
                        <span>Seats: <b className="text-slate-800">{subInfo.subscription.seats_purchased || 0}</b></span>
                        <span>Sub #: <b className="text-slate-800">{subInfo.subscription.subscription_number}</b></span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {subInfo?.subscription && (() => {
                    const st = subInfo.effective_status || subInfo.subscription.subscription_status || "";
                    const map = {
                      active: "bg-emerald-50 text-emerald-700 border-emerald-200",
                      in_grace: "bg-amber-50 text-amber-700 border-amber-200",
                      expired: "bg-rose-50 text-rose-700 border-rose-200",
                      inactive: "bg-slate-100 text-slate-600 border-slate-200",
                      cancelled: "bg-slate-100 text-slate-600 border-slate-200",
                      none: "bg-slate-100 text-slate-500 border-slate-200",
                    };
                    return (
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${map[st] || map.none}`} data-testid="admin-org-sub-status">
                        {st === "in_grace" ? "IN GRACE" : st.toUpperCase()}
                        {typeof subInfo.days_remaining === "number" && (st === "active" || st === "in_grace") && (
                          <span className="ml-1 text-slate-500">· {subInfo.days_remaining}d left</span>
                        )}
                      </span>
                    );
                  })()}
                  <button onClick={openPlanDialog} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0c135c]" data-testid="admin-org-assign-plan-btn">
                    <Package className="h-3.5 w-3.5" /> {subInfo?.subscription ? "Change plan" : "Assign plan"}
                  </button>
                  {subInfo?.subscription && subInfo.subscription.subscription_status === "active" && (
                    <button onClick={() => setDeactivateOpen(true)} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50" data-testid="admin-org-sub-deactivate-btn">
                      <PowerOff className="h-3.5 w-3.5" /> Deactivate
                    </button>
                  )}
                </div>
              </div>
              {subInfo?.effective_status === "in_grace" && (
                <div className="mt-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                  In 7-day grace period · auto-deactivates in {subInfo.days_remaining} day{subInfo.days_remaining === 1 ? "" : "s"} unless renewed.
                </div>
              )}
              {subInfo?.effective_status === "expired" && (
                <div className="mt-4 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800">
                  <AlertTriangle className="h-4 w-4" />
                  Subscription has expired · new enrolments and paid features are paused.
                </div>
              )}
            </div>

            {/* Bulk invoice for institute — Super Admin can bill the hospital for a mapped course */}
            <div className="mt-4 rounded-2xl border-2 border-[#329112] bg-gradient-to-br from-white to-emerald-50 p-6" data-testid="admin-org-bulk-bill-card">              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#329112]/10 text-[#329112]"><Receipt className="h-5 w-5" /></div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Bulk invoice · Institute billing</div>
                    <div className="text-sm font-bold text-slate-900">Bill this institute for a mapped course</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Creates one consolidated PAID invoice with a line per enrolled staff,
                      auto-enrols them, and emails the PDF to the institute.
                    </div>
                  </div>
                </div>
                <button onClick={openBillDialog} className="inline-flex items-center gap-1.5 rounded-md bg-[#329112] px-4 py-2 text-sm font-bold text-white hover:bg-[#256b0d]" data-testid="admin-org-bulk-bill-btn">
                  <Receipt className="h-4 w-4" /> New bulk invoice
                </button>
              </div>
            </div>

            {/* Subscription history — full audit trail of every plan change/renewal */}
            {subHistory.length > 0 && (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-org-sub-history">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876]"><ClipboardCheck className="h-4 w-4" /></div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Subscription history</div>
                    <div className="text-xs text-slate-500">{subHistory.length} record{subHistory.length === 1 ? "" : "s"} · Every plan change, renewal, and linked invoice</div>
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-100">
                        <th className="py-2">Sub #</th>
                        <th className="py-2">Plan</th>
                        <th className="py-2">Period</th>
                        <th className="py-2 text-right">Seats</th>
                        <th className="py-2 text-right">Total</th>
                        <th className="py-2">Status</th>
                        <th className="py-2">Invoice</th>
                        <th className="py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {subHistory.map((h) => {
                        const eff = h.effective_status || h.subscription_status || "";
                        const invStatus = (h.invoice_status || "").toLowerCase();
                        const tone = invStatus === "deleted" ? "bg-slate-100 text-slate-500 border-slate-200"
                          : invStatus === "cancelled" ? "bg-rose-50 text-rose-700 border-rose-200"
                          : eff === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : eff === "in_grace" ? "bg-amber-50 text-amber-700 border-amber-200"
                          : eff === "expired" ? "bg-rose-50 text-rose-700 border-rose-200"
                          : "bg-slate-50 text-slate-600 border-slate-200";
                        const statusLabel = invStatus === "deleted" ? "DELETED"
                          : invStatus === "cancelled" ? "CANCELLED"
                          : eff === "in_grace" ? "IN GRACE" : eff;
                        const isDeleted = invStatus === "deleted";
                        const isCancelled = invStatus === "cancelled";
                        return (
                          <tr key={h.id} data-testid={`admin-org-sub-row-${h.id}`} className={isDeleted ? "text-slate-400" : ""}>
                            <td className="py-2 font-mono text-[11px] text-slate-700">{h.subscription_number}</td>
                            <td className="py-2">
                              <div className="text-xs font-semibold text-slate-900">{h.plan_name}</div>
                              {h.is_renewal && <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-700">Renewal</div>}
                            </td>
                            <td className="py-2 text-[11px] text-slate-600">
                              {(h.start_date || "").slice(0, 10)} → {(h.end_date || "").slice(0, 10)}
                            </td>
                            <td className="py-2 text-right text-xs font-semibold">{h.seats_purchased || 0}</td>
                            <td className="py-2 text-right text-xs">
                              <div className="font-semibold">₹{Number(h.total_amount || 0).toLocaleString("en-IN")}</div>
                              {Number(h.discount_amount || 0) > 0 && (
                                <div className="text-[10px] text-emerald-700">−₹{Number(h.discount_amount).toLocaleString("en-IN")}</div>
                              )}
                            </td>
                            <td className="py-2">
                              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tone}`}>
                                {statusLabel}
                              </span>
                            </td>
                            <td className="py-2 text-[11px]">
                              {h.invoice_number ? (
                                <span className="font-mono text-slate-700" data-testid={`admin-org-sub-inv-${h.id}`}>{h.invoice_number}</span>
                              ) : (
                                <span className="italic text-slate-400">—</span>
                              )}
                            </td>
                            <td className="py-2 text-right">
                              {h.invoice_id && !isCancelled && !isDeleted && (
                                <button type="button" onClick={() => onInvoiceAction("cancel", h)}
                                        data-testid={`admin-org-sub-cancel-${h.id}`}
                                        className="mr-1 inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] font-bold text-rose-700 hover:bg-rose-100">
                                  Cancel
                                </button>
                              )}
                              {h.invoice_id && !isDeleted && (
                                <button type="button" onClick={() => onInvoiceAction("delete", h)}
                                        data-testid={`admin-org-sub-delete-${h.id}`}
                                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[10px] font-bold text-slate-700 hover:bg-slate-50">
                                  Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-4 grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Onboarding progress</div>
                <div className="mt-2 flex items-center gap-3">
                  <Progress value={onboarding?.completion_percentage || 0} className="h-2 flex-1" />
                  <span className="text-sm font-bold text-slate-900">{onboarding?.completion_percentage || 0}%</span>
                </div>
                <ul className="mt-6 space-y-2">
                  {ONBOARDING_LABELS.map(([key, label]) => {
                    const done = ACTIVE_KEYS.has(key) ? onboarding?.[key] : false;
                    return (
                      <li key={key} className="flex items-center gap-3 text-sm">
                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${done ? "bg-[#329112] text-white" : "bg-slate-100 text-slate-400"}`}>
                          <ClipboardCheck className="h-3 w-3" />
                        </span>
                        <span className={done ? "text-slate-900" : "text-slate-500"}>{label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Primary admin</div>
                {primaryAdmin ? (
                  <div className="mt-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#101876] text-xs font-bold text-white">
                        {(primaryAdmin.first_name?.[0] || "") + (primaryAdmin.last_name?.[0] || "")}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-900">{primaryAdmin.full_name}</div>
                        <div className="text-xs text-slate-500">{primaryAdmin.email}</div>
                      </div>
                    </div>
                    <Link to={`/app/super-admin/users/${primaryAdmin.id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#101876] hover:underline">
                      Open user <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-slate-500">No admin assigned yet.</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Departments */}
          <TabsContent value="departments">
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                <div className="text-sm font-bold text-slate-900">Departments ({departments.length})</div>
                <div className="flex items-center gap-2">
                  {departments.length === 0 && (
                    <button onClick={seedDefaultDepts} className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-[#329112]/40 bg-[#329112]/5 px-3 py-1.5 text-xs font-semibold text-[#329112]">
                      <ClipboardCheck className="h-3.5 w-3.5" /> Seed 18 healthcare departments
                    </button>
                  )}
                  <button onClick={openDeptCreate} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0c135c]" data-testid="admin-org-dept-add-btn">
                    <Plus className="h-3.5 w-3.5" /> Add department
                  </button>
                </div>
              </div>
              {departments.length === 0 ? (
                <EmptyState title="No departments yet" description="Create departments manually or seed the default healthcare set." icon={Building} />
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Code</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                    <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {departments.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell className="font-semibold text-slate-900">{d.department_name}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-600">{d.department_code}</TableCell>
                        <TableCell className="text-sm text-slate-600">{d.description || "—"}</TableCell>
                        <TableCell><StatusBadge status={d.status} /></TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => openDeptEdit(d)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]"><Edit3 className="h-3.5 w-3.5" /></button>
                            <button onClick={() => toggleDept(d)} className="inline-flex h-7 items-center justify-center rounded-md px-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-[#101876]">
                              {d.status === "active" ? "Deactivate" : "Activate"}
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Admins */}
          <TabsContent value="admins">
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                <div className="text-sm font-bold text-slate-900">Institute admins ({admins.length})</div>
                <button onClick={openAssign} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0c135c]">
                  <UserCog className="h-3.5 w-3.5" /> Assign admin
                </button>
              </div>
              {admins.length === 0 ? (
                <EmptyState title="No admin yet" description="Assign an existing hospital admin or create a new one." icon={UsersRound} />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {admins.map((a) => (
                    <li key={a.id} className="flex items-center justify-between p-4">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-slate-900">{a.full_name}</div>
                        <div className="text-xs text-slate-500">{a.email} · {a.mobile}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={a.account_status} />
                        <Link to={`/app/super-admin/users/${a.id}`} className="text-xs font-semibold text-[#101876] hover:underline">Open</Link>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* Branding */}
          <TabsContent value="branding">
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-org-branding">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876]"><Palette className="h-5 w-5" /></div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Branding</div>
                  <div className="text-sm font-bold text-slate-900">Logo, colors and future white-label placeholders</div>
                </div>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Logo URL</Label>
                  <Input value={branding.logo || ""} onChange={(e) => setBranding({ ...branding, logo: e.target.value })} placeholder="https://cdn.example.com/logo.png" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Primary color</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={branding.primary_color || "#101876"} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
                    <Input value={branding.primary_color || ""} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Secondary color</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={branding.secondary_color || "#329112"} onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
                    <Input value={branding.secondary_color || ""} onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Login banner URL</Label>
                  <Input value={branding.login_banner || ""} onChange={(e) => setBranding({ ...branding, login_banner: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Certificate logo URL</Label>
                  <Input value={branding.certificate_logo || ""} onChange={(e) => setBranding({ ...branding, certificate_logo: e.target.value })} placeholder="Phase 8 will use this" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Footer text</Label>
                  <Input value={branding.footer_text || ""} onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Custom domain (Phase 13 placeholder)</Label>
                  <Input value={branding.custom_domain_placeholder || ""} onChange={(e) => setBranding({ ...branding, custom_domain_placeholder: e.target.value })} placeholder="learn.hospital.com" />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={submitBranding} disabled={savingBranding} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="admin-org-branding-save">
                  {savingBranding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Palette className="h-4 w-4" />} Save branding
                </button>
              </div>
            </div>
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents">
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#101876]" />
                  <div className="text-sm font-bold text-slate-900">Documents ({documents.length})</div>
                </div>
                <button onClick={() => setDocDlg(true)} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0c135c]">
                  <Plus className="h-3.5 w-3.5" /> Add document
                </button>
              </div>
              {documents.length === 0 ? (
                <EmptyState title="No documents added" description="Track MoU, GST, PAN, NABH certificates and more." icon={FileText} />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {documents.map((d) => (
                    <li key={d.id} className="flex items-center justify-between p-4">
                      <div>
                        <div className="text-sm font-bold text-slate-900">{d.document_name}</div>
                        <div className="text-xs text-slate-500">{d.document_type} · {d.remarks || "—"}</div>
                        {d.document_file_path && <a href={d.document_file_path} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#101876] hover:underline">Open file <ExternalLink className="h-3 w-3" /></a>}
                      </div>
                      <button onClick={() => deleteDoc(d.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* Status history */}
          <TabsContent value="history">
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
                <ScrollText className="h-4 w-4 text-[#101876]" />
                <div className="text-sm font-bold text-slate-900">Status history</div>
              </div>
              {history.length === 0 ? (
                <EmptyState title="No status changes yet" description="Suspending or reactivating this org will show here." icon={ScrollText} />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {history.map((h) => (
                    <li key={h.id} className="p-4">
                      <div className="text-sm font-bold text-slate-900">
                        <span className="text-slate-500">{h.old_status || "—"}</span> → {h.new_status}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">{h.reason}</div>
                      <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">
                        {new Date(h.changed_at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Status change dialog */}
      <Dialog open={!!statusDlg} onOpenChange={(o) => { if (!o) { setStatusDlg(null); setStatusReason(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{statusDlg?.title}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Reason (required)</Label>
            <Textarea rows={3} value={statusReason} onChange={(e) => setStatusReason(e.target.value)} data-testid="admin-org-status-reason" placeholder="Why is this happening? Recorded in audit + notifications." />
          </div>
          <DialogFooter>
            <button onClick={() => { setStatusDlg(null); setStatusReason(""); }} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={applyStatusChange} disabled={savingStatus} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="admin-org-status-confirm">
              {savingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign admin dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>Assign hospital / institute admin</DialogTitle></DialogHeader>
          {assignError && <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs font-semibold text-rose-700">{assignError}</div>}
          <div className="flex gap-2 rounded-md bg-slate-100 p-1 text-xs font-semibold">
            <button onClick={() => setAssignMode("create")} className={`flex-1 rounded-md px-3 py-1.5 ${assignMode === "create" ? "bg-white text-[#101876] shadow" : "text-slate-500"}`}>Create new</button>
            <button onClick={() => setAssignMode("existing")} className={`flex-1 rounded-md px-3 py-1.5 ${assignMode === "existing" ? "bg-white text-[#101876] shadow" : "text-slate-500"}`}>Assign existing</button>
          </div>
          {assignMode === "create" ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">First name</Label><Input value={assignForm.first_name} onChange={(e) => setAssignForm({ ...assignForm, first_name: e.target.value })} data-testid="assign-admin-first" /></div>
              <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Last name</Label><Input value={assignForm.last_name} onChange={(e) => setAssignForm({ ...assignForm, last_name: e.target.value })} data-testid="assign-admin-last" /></div>
              <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label><Input type="email" value={assignForm.email} onChange={(e) => setAssignForm({ ...assignForm, email: e.target.value })} data-testid="assign-admin-email" /></div>
              <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Mobile</Label><Input value={assignForm.mobile} onChange={(e) => setAssignForm({ ...assignForm, mobile: e.target.value })} data-testid="assign-admin-mobile" /></div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Designation</Label><Input value={assignForm.designation} onChange={(e) => setAssignForm({ ...assignForm, designation: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Password</Label><Input type="password" value={assignForm.password} onChange={(e) => setAssignForm({ ...assignForm, password: e.target.value })} data-testid="assign-admin-password" /></div>
              <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Confirm password</Label><Input type="password" value={assignForm.confirm} onChange={(e) => setAssignForm({ ...assignForm, confirm: e.target.value })} data-testid="assign-admin-confirm" /></div>
            </div>
          ) : (
            <div className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Select an existing Hospital Admin</Label>
              {existingAdmins.length === 0 ? <div className="text-xs text-slate-500">No unassigned hospital admins available.</div> : (
                <Select value={assignForm.user_id} onValueChange={(v) => setAssignForm({ ...assignForm, user_id: v })}>
                  <SelectTrigger data-testid="assign-admin-existing-select"><SelectValue placeholder="Choose an admin…" /></SelectTrigger>
                  <SelectContent>
                    {existingAdmins.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} — {u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setAssignOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitAssign} disabled={assignSaving} className="inline-flex items-center gap-2 rounded-md bg-[#329112] px-4 py-2 text-sm font-semibold text-white hover:bg-[#256b0d] disabled:opacity-70" data-testid="assign-admin-submit">
              {assignSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {assignMode === "create" ? "Create & assign" : "Assign"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Department dialog */}
      <Dialog open={!!deptDlg} onOpenChange={(o) => !o && setDeptDlg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{deptDlg?.create ? "Add department" : `Edit ${deptDlg?.department_name}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Name</Label><Input value={deptForm.department_name} onChange={(e) => setDeptForm({ ...deptForm, department_name: e.target.value })} data-testid="dept-dlg-name" /></div>
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Code</Label><Input value={deptForm.department_code} onChange={(e) => setDeptForm({ ...deptForm, department_code: e.target.value })} data-testid="dept-dlg-code" /></div>
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Description</Label><Textarea rows={2} value={deptForm.description} onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setDeptDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitDept} disabled={deptSaving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="dept-dlg-submit">
              {deptSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {deptDlg?.create ? "Create" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document dialog */}
      <Dialog open={docDlg} onOpenChange={setDocDlg}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Type</Label>
              <Select value={docForm.document_type} onValueChange={(v) => setDocForm({ ...docForm, document_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["MoU","Agreement","Registration Certificate","GST Certificate","PAN","NABH Certificate","Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Name</Label><Input value={docForm.document_name} onChange={(e) => setDocForm({ ...docForm, document_name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">File URL (Phase 3 placeholder)</Label><Input value={docForm.document_file_path} onChange={(e) => setDocForm({ ...docForm, document_file_path: e.target.value })} placeholder="https://" /></div>
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Remarks</Label><Textarea rows={2} value={docForm.remarks} onChange={(e) => setDocForm({ ...docForm, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setDocDlg(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitDoc} disabled={docSaving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70">
              {docSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Add
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign / Change Subscription Plan dialog */}
      <Dialog open={planDlgOpen} onOpenChange={setPlanDlgOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{subInfo?.subscription ? "Change" : "Assign"} subscription plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Plan</Label>
              <Select value={planForm.plan_id} onValueChange={(v) => {
                const chosen = plans.find((p) => p.id === v);
                setPlanForm((f) => ({
                  ...f, plan_id: v,
                  seats_purchased: chosen?.max_users || f.seats_purchased || 50,
                }));
              }}>
                <SelectTrigger data-testid="admin-org-plan-select"><SelectValue placeholder="Pick a plan…" /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.plan_name} · ₹{p.price?.toLocaleString?.() || p.price} · {p.max_users || 0} users · {p.validity_days || 365}d
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Seats purchased</Label>
                <Input type="number" min={1} value={planForm.seats_purchased}
                  onChange={(e) => setPlanForm({ ...planForm, seats_purchased: e.target.value })}
                  data-testid="admin-org-plan-seats" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Payment status</Label>
                <Select value={planForm.payment_status} onValueChange={(v) => setPlanForm({ ...planForm, payment_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Start date</Label>
                <Input type="date" value={planForm.start_date}
                  onChange={(e) => setPlanForm({ ...planForm, start_date: e.target.value })}
                  data-testid="admin-org-plan-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">End date (auto)</Label>
                <Input type="date" value={planForm.end_date}
                  onChange={(e) => setPlanForm({ ...planForm, end_date: e.target.value })}
                  placeholder="Auto = start + validity days"
                  data-testid="admin-org-plan-end" />
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="admin-org-plan-coupon">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Coupon code</Label>
              <div className="flex gap-2">
                <Input value={planForm.coupon_code}
                  onChange={(e) => { setPlanForm({ ...planForm, coupon_code: e.target.value.toUpperCase() }); setCouponPreview(null); setCouponError(""); }}
                  placeholder="e.g. LOYAL20 · flat or %"
                  className="uppercase"
                  data-testid="admin-org-plan-coupon-input" />
                {couponPreview ? (
                  <button type="button" onClick={clearCoupon}
                    className="rounded-md border border-rose-200 bg-white px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50"
                    data-testid="admin-org-plan-coupon-clear">
                    Remove
                  </button>
                ) : (
                  <button type="button" onClick={applyCoupon} disabled={validatingCoupon || !planForm.coupon_code}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#329112] px-3 py-2 text-xs font-bold text-white hover:bg-[#256b0d] disabled:opacity-50"
                    data-testid="admin-org-plan-coupon-apply">
                    {validatingCoupon ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                    Apply
                  </button>
                )}
              </div>
              {couponError && (
                <div className="text-xs font-semibold text-rose-600" data-testid="admin-org-plan-coupon-err">{couponError}</div>
              )}
              {couponPreview && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-800" data-testid="admin-org-plan-coupon-preview">
                  <b>{couponPreview.coupon.coupon_code}</b> — {couponPreview.coupon.coupon_name || (couponPreview.coupon.discount_type === "percentage" ? `${couponPreview.coupon.discount_value}% off` : `₹${couponPreview.coupon.discount_value} off`)}
                  <div className="mt-0.5 text-emerald-900">
                    ₹{Number(couponPreview.price).toLocaleString("en-IN")} − ₹{Number(couponPreview.discount).toLocaleString("en-IN")} = <b>₹{Number(couponPreview.total).toLocaleString("en-IN")}</b> incl. GST
                  </div>
                </div>
              )}
            </div>

            {subInfo?.subscription && (() => {
              const chosen = plans.find((p) => p.id === planForm.plan_id);
              const price = chosen?.price || 0;
              const pct = Number(planForm.discount_percent) || 0;
              const disc = Math.round((price * pct) / 100);
              const finalAmt = Math.max(0, price - disc);
              return (
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="admin-org-plan-loyalty">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold uppercase tracking-widest text-emerald-800">
                      Loyalty discount (renewal only)
                    </Label>
                    <span className="text-[10px] font-semibold text-emerald-700">Repeat customer</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {[0, 5, 10, 15, 20].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setPlanForm({ ...planForm, discount_percent: v })}
                        data-testid={`admin-org-loyalty-${v}`}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${pct === v ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"}`}
                      >
                        {v}%
                      </button>
                    ))}
                    <Input
                      type="number" min={0} max={100}
                      value={planForm.discount_percent}
                      onChange={(e) => setPlanForm({ ...planForm, discount_percent: e.target.value })}
                      className="w-20 text-xs"
                      data-testid="admin-org-loyalty-input"
                    />
                    <span className="text-xs font-semibold text-emerald-800">%</span>
                  </div>
                  {price > 0 && (
                    <div className="text-xs text-emerald-800">
                      ₹{price.toLocaleString("en-IN")} − ₹{disc.toLocaleString("en-IN")} discount = <b>₹{finalAmt.toLocaleString("en-IN")}</b> before GST
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              After the end date passes, a 7-day grace period runs. Once grace ends, the subscription auto-deactivates and blocks new seat usage.
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setPlanDlgOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitPlan} disabled={planSaving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="admin-org-plan-save">
              {planSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
              {subInfo?.subscription ? "Save change" : "Assign plan"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk invoice dialog — Super Admin billing this institute */}
      <Dialog open={billOpen} onOpenChange={setBillOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader><DialogTitle>Bulk invoice this institute</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Course *</Label>
                <Select value={billForm.course_id} onValueChange={(v) => setBillForm({ ...billForm, course_id: v })}>
                  <SelectTrigger data-testid="admin-bulk-bill-course"><SelectValue placeholder={mappedCourses.length ? "Pick a mapped course…" : "No courses mapped yet"} /></SelectTrigger>
                  <SelectContent>
                    {mappedCourses.map((m) => (
                      <SelectItem key={m.course_id} value={m.course_id}>
                        {m.course?.course_title || m.course_name || m.course_title || m.course_id}
                        {m.course?.course_code ? ` · ${m.course.course_code}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Unit price (₹) *</Label>
                <Input type="number" min={0} value={billForm.unit_price}
                  onChange={(e) => setBillForm({ ...billForm, unit_price: e.target.value })}
                  data-testid="admin-bulk-bill-price" placeholder="e.g. 1500" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Payment method *</Label>
                <Select value={billForm.payment_method} onValueChange={(v) => setBillForm({ ...billForm, payment_method: v })}>
                  <SelectTrigger data-testid="admin-bulk-bill-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="google_pay">Google Pay</SelectItem>
                    <SelectItem value="phonepe">PhonePe</SelectItem>
                    <SelectItem value="paytm">Paytm</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Reference / UTR</Label>
                <Input value={billForm.payment_reference}
                  onChange={(e) => setBillForm({ ...billForm, payment_reference: e.target.value })}
                  data-testid="admin-bulk-bill-ref" placeholder="Optional" />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Staff to enrol</span>
                  <span className="rounded-full bg-[#329112] px-2 py-0.5 text-[10px] font-bold text-white" data-testid="admin-bulk-bill-selected-count">
                    {selectedCount} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setAllStaff(true)} data-testid="admin-bulk-bill-select-all" className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#101876]/40 hover:text-[#101876]">Select all</button>
                  <button type="button" onClick={() => setAllStaff(false)} data-testid="admin-bulk-bill-clear-all" className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-rose-200 hover:text-rose-700">Clear</button>
                </div>
              </div>
              <div className="mt-2 relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input value={staffQuery} onChange={(e) => setStaffQuery(e.target.value)}
                  placeholder="Search by name, email, department, designation…"
                  className="pl-8" data-testid="admin-bulk-bill-search" />
              </div>
              <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white" data-testid="admin-bulk-bill-staff-list">
                {filteredStaff.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-500">No staff match.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredStaff.map((s) => (
                      <li key={s.id} className="flex items-center gap-3 px-3 py-2">
                        <input type="checkbox" checked={!!selectedStaff[s.id]}
                          onChange={() => toggleStaff(s.id)}
                          className="h-4 w-4" data-testid={`admin-bulk-bill-staff-${s.id}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{s.full_name || s.email}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {s.email}{s.department_name ? ` · ${s.department_name}` : ""}{s.designation ? ` · ${s.designation}` : ""}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {selectedCount > 0 && Number(billForm.unit_price) > 0 && (
              <div className="rounded-lg border-2 border-[#329112] bg-[#329112]/5 p-3 text-sm" data-testid="admin-bulk-bill-summary">
                <CheckCircle2 className="inline h-4 w-4 text-[#329112] mr-1" />
                Billing hospital for <b>{selectedCount}</b> staff × ₹{Number(billForm.unit_price).toLocaleString("en-IN")} = <b>₹{(selectedCount * Number(billForm.unit_price)).toLocaleString("en-IN")}</b> via <b className="capitalize">{billForm.payment_method.replace(/_/g, " ")}</b>.
              </div>
            )}

            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={billForm.send_email}
                onChange={(e) => setBillForm({ ...billForm, send_email: e.target.checked })}
                data-testid="admin-bulk-bill-email" />
              Email invoice PDF to the institute
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setBillOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitBulkBill} disabled={billing || selectedCount === 0 || !Number(billForm.unit_price) || !billForm.course_id}
              className="inline-flex items-center gap-2 rounded-md bg-[#329112] px-4 py-2 text-sm font-bold text-white hover:bg-[#256b0d] disabled:opacity-50"
              data-testid="admin-bulk-bill-submit">
              {billing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
              Create invoice · {selectedCount} staff
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate subscription confirmation */}
      <ConfirmDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        title="Deactivate subscription?"
        description="This immediately marks the subscription as inactive. Staff seats will stop counting and new paid features will be blocked until you assign a new plan."
        confirmLabel={deactivating ? "Deactivating…" : "Deactivate"}
        destructive
        onConfirm={confirmDeactivate}
        testKey="admin-org-sub-deactivate-dlg"
      />

      {/* Invoice cancel / delete */}
      <Dialog open={!!invAction} onOpenChange={(o) => { if (!o) closeInvAction(); }}>
        <DialogContent className="sm:max-w-md" data-testid="admin-org-sub-inv-dialog">
          <DialogHeader>
            <DialogTitle>
              {invAction?.kind === "cancel" ? "Cancel invoice" : "Delete invoice"}{" "}
              {invAction?.row?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-slate-600">
              {invAction?.kind === "cancel"
                ? "Keeps the invoice number in the sequence, stamps the PDF as CANCELLED, and voids the linked subscription."
                : "Replaces the invoice with a DELETED tombstone (sequence stays gap-free), removes the order and voids the linked subscription. The PDF will no longer be downloadable."}
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Reason (optional)</Label>
              <Textarea value={invReason} onChange={(e) => setInvReason(e.target.value)}
                        data-testid="admin-org-sub-inv-reason" rows={3}
                        placeholder="e.g. Wrong buyer details, duplicate entry, etc." />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={invNotify} onChange={(e) => setInvNotify(e.target.checked)}
                     data-testid="admin-org-sub-inv-notify" />
              <span>Notify institute by email</span>
            </label>
          </div>
          <DialogFooter>
            <button type="button" onClick={closeInvAction} disabled={invSubmitting}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Back
            </button>
            <button type="button" onClick={confirmInvAction} disabled={invSubmitting}
                    data-testid="admin-org-sub-inv-confirm"
                    className={`inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-bold text-white ${invAction?.kind === "cancel" ? "bg-rose-600 hover:bg-rose-700" : "bg-slate-800 hover:bg-slate-900"}`}>
              {invSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {invAction?.kind === "cancel" ? "Cancel invoice" : "Delete invoice"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
}

